import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Button, Card, Text, useTheme, Portal, Modal, ActivityIndicator, FAB, IconButton } from 'react-native-paper';
import { modeloMensagemService } from '../../services/modeloMensagemService';
import { supabase } from '../../services/supabase';

export type ModeloMensagem = {
  id: number;
  codempresa: number | null;  // Pode ser nulo para modelos padrão
  modelo: string;
  n8n: boolean;
};

export function ListaModelos({ navigation }: any) {
  const [modelos, setModelos] = useState<Array<{
    id: number;
    codempresa: number | null;  // Pode ser nulo para modelos padrão
    modelo: string;
    n8n: boolean;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [empresa, setEmpresa] = useState<{ id: number; nome: string } | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [modeloToDelete, setModeloToDelete] = useState<ModeloMensagem | null>(null);
  const theme = useTheme();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obter usuário e empresa
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Primeiro busca o ID da empresa do usuário
      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.empresa) throw new Error('Empresa não encontrada');

      // Depois busca o nome da empresa
      if (userData.empresa) {
        const { data: empresaData, error: empresaError } = await supabase
          .from('empresa')
          .select('nome')
          .eq('id', userData.empresa)
          .maybeSingle();

        if (empresaError) {
          console.error('Erro ao buscar nome da empresa:', empresaError);
          throw empresaError;
        }

        setEmpresa({
          id: userData.empresa,
          nome: empresaData?.nome || 'Empresa sem nome'
        });
      } else {
        console.error('ID da empresa não encontrado');
        setEmpresa({
          id: 0,
          nome: 'Empresa não encontrada'
        });
      }

      // Buscar modelos da empresa do usuário
      try {
        const modelosData = await modeloMensagemService.getModelos(userData.empresa);
        const modelosFormatados = modelosData.map(modelo => ({
          id: modelo.id || 0,
          codempresa: modelo.codempresa,
          modelo: modelo.modelo,
          n8n: modelo.n8n
        }));
        setModelos(modelosFormatados);
      } catch (err) {
        console.error('Erro ao buscar modelos:', err);
        setError('Erro ao carregar os modelos da empresa');
        setModelos([]);
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Erro ao carregar modelos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchData();
    });

    return unsubscribe;
  }, [navigation]);

  const handleEditar = (modelo: ModeloMensagem) => {
    navigation.navigate('EditarModelo', { modelo });
  };

  const handleNovoModelo = () => {
    navigation.navigate('NovoModelo');
  };

  const confirmarExclusao = (modelo: ModeloMensagem) => {
    setModeloToDelete(modelo);
    setDeleteModalVisible(true);
  };

  const handleExcluir = async () => {
    if (!modeloToDelete) return;
    
    try {
      await modeloMensagemService.deleteModelo(modeloToDelete.id);
      setModelos(modelos.filter(m => m.id !== modeloToDelete.id));
      setDeleteModalVisible(false);
      setModeloToDelete(null);
    } catch (error: any) {
      Alert.alert('Erro', 'Não foi possível excluir o modelo: ' + error.message);
    }
  };

  const getTipoModelo = (n8n: boolean) => {
    return n8n ? 'Múltiplos Títulos' : 'Título Único';
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Carregando modelos...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Erro ao carregar modelos: {error}</Text>
        <Button mode="contained" onPress={fetchData} style={styles.retryButton}>
          Tentar Novamente
        </Button>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView>
        {modelos.length === 0 ? (
          <Card style={styles.emptyCard}>
            <Card.Content>
              <Text style={styles.emptyText}>Nenhum modelo cadastrado</Text>
              <Button 
                mode="contained" 
                onPress={handleNovoModelo}
                style={styles.addButton}
              >
                Adicionar Modelo
              </Button>
            </Card.Content>
          </Card>
        ) : (
          modelos.map((modelo) => (
            <Card key={modelo.id} style={styles.card}>
              <Card.Title
                title={`Modelo ${modelo.id}`}
                subtitle={`Tipo: ${getTipoModelo(modelo.n8n)}`}
                right={(props) => (
                  <View style={styles.cardActions}>
                    <IconButton
                      {...props}
                      icon="pencil"
                      onPress={() => handleEditar(modelo)}
                      size={20}
                    />
                    <IconButton
                      {...props}
                      icon="delete"
                      onPress={() => confirmarExclusao(modelo)}
                      size={20}
                      iconColor={theme.colors.error}
                    />
                  </View>
                )}
              />
              <Card.Content>
                <Text 
                  numberOfLines={3} 
                  ellipsizeMode="tail"
                  style={styles.modeloPreview}
                >
                  {modelo.modelo}
                </Text>
              </Card.Content>
            </Card>
          ))
        )}
      </ScrollView>

      <FAB
        style={styles.fab}
        icon="plus"
        onPress={handleNovoModelo}
        color="white"
      />

      {/* Modal de confirmação de exclusão */}
      <Portal>
        <Modal
          visible={deleteModalVisible}
          onDismiss={() => setDeleteModalVisible(false)}
          contentContainerStyle={styles.modalContainer}
        >
          <Card>
            <Card.Title
              title="Confirmar Exclusão"
              titleStyle={styles.modalTitle}
            />
            <Card.Content>
              <Text style={styles.modalText}>
                Tem certeza que deseja excluir este modelo? Esta ação não pode ser desfeita.
              </Text>
            </Card.Content>
            <Card.Actions style={styles.modalActions}>
              <Button onPress={() => setDeleteModalVisible(false)}>
                Cancelar
              </Button>
              <Button onPress={handleExcluir} color={theme.colors.error}>
                Excluir
              </Button>
            </Card.Actions>
          </Card>
        </Modal>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    color: 'red',
    marginBottom: 20,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 10,
  },
  emptyCard: {
    margin: 16,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  emptyText: {
    textAlign: 'center',
    marginBottom: 20,
    fontSize: 16,
  },
  addButton: {
    marginTop: 10,
  },
  card: {
    margin: 8,
    elevation: 2,
  },
  cardActions: {
    flexDirection: 'row',
  },
  modeloPreview: {
    color: '#666',
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
    backgroundColor: '#6200ee',
  },
  modalContainer: {
    backgroundColor: 'white',
    padding: 20,
    margin: 20,
    borderRadius: 8,
  },
  modalTitle: {
    color: '#d32f2f',
  },
  modalText: {
    marginBottom: 16,
    fontSize: 16,
  },
  modalActions: {
    justifyContent: 'flex-end',
  },
});
